import {useMemo} from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {InputField} from '@/pages/components/common';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import type {VJournalPage} from '@/pages/components/audit-program/v-section/constants';
import {V2_1_PAGE} from '@/pages/components/audit-program/v-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface VJournalTestViewProps {
    header: AuditProgramSectionVO;
    page: VJournalPage;
    refCode: string;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

const cellBorder = {borderColor: 'divider'};
const headSx = {...cellBorder, fontWeight: 600, fontSize: '0.7rem', py: 0.6, px: 0.35};
const bodySx = {...cellBorder, py: 0.35, px: 0.3, verticalAlign: 'top' as const, overflow: 'hidden'};
const fieldSx = {minWidth: 0, width: '100%', maxWidth: '100%'};

const COLS = [
    {id: 'date', label: 'Date of journal', align: 'left' as const},
    {id: 'amount', label: 'Amount (HK$)', align: 'right' as const},
    {id: 'description', label: 'Description', align: 'left' as const},
    {id: 'processedBy', label: 'Processed by', align: 'left' as const},
    {id: 'authorizedBy', label: 'Authorized by', align: 'left' as const},
    {id: 'approach', label: 'Approach to verification', align: 'left' as const},
    {id: 'evidence', label: 'Evidence inspected', align: 'left' as const},
];

/** V2.1 / V4.1 — Detailed testing of journals. */
export default function VJournalTestView({
    header,
    page,
    refCode,
    values,
    onChange,
}: VJournalTestViewProps) {
    const meta = {
        title: page.title || V2_1_PAGE.title,
        note: page.note || V2_1_PAGE.note,
        blankRows: page.blankRows || V2_1_PAGE.blankRows,
    };
    const rowIds = useMemo(
        () => Array.from({length: Math.max(1, meta.blankRows)}, (_, i) => `j${i + 1}`),
        [meta.blankRows],
    );

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a12"
                refCode={refCode}
                values={values}
                onChange={onChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.3, mb: 0.5, mt: 0.5}}
            >
                {meta.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{mb: 1.5, textAlign: 'center'}}>
                {meta.note}
            </Typography>

            <TableContainer sx={{mb: 2, overflowX: 'auto'}}>
                <Table size="small" sx={{minWidth: 980, tableLayout: 'fixed', borderCollapse: 'collapse'}}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx} style={{width: 32}}/>
                            {COLS.map((c) => (
                                <TableCell key={c.id} sx={headSx}>{c.label}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rowIds.map((id, idx) => (
                            <TableRow key={id}>
                                <TableCell sx={bodySx}>
                                    <Typography variant="body2" color="text.secondary">{idx + 1}</Typography>
                                </TableCell>
                                {COLS.map((c) => (
                                    <TableCell key={c.id} sx={bodySx}>
                                        <InputField
                                            type="text"
                                            fullWidth
                                            compact
                                            align={c.align}
                                            sx={fieldSx}
                                            value={values[`${id}.${c.id}`] ?? ''}
                                            onChange={(e) => onChange(`${id}.${c.id}`, e.target.value)}
                                        />
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
